import type React from 'react';
import { cx } from '@/styled-system/css';
import type { CommuteDestination, CommuteTime, TravelMode } from '../utils/commuteTypes';
import { formatDuration } from '../utils/routeCalculation';
import { BicyclingIcon, DrivingIcon, TransitIcon, WalkingIcon } from '../../../../ui/icons';

// Define the icon component type
type IconComponent = React.FC<{
  size?: string | number;
  color?: string;
  className?: string;
}>;

// Define the travel mode configuration type
type TravelModeConfig = {
  IconComponent: IconComponent;
  iconColor: string;
  label: string;
  fallbackTime: string;
};
import {
  clearCommuteButton,
  commuteCard,
  commuteCardActive,
  commuteCardContent,
  commuteCardIcon,
  commuteCardMode,
  commuteCardTime,
  commuteResultsContainer,
  commuteResultsGridWithMargin,
  commuteResultsHeader,
  commuteTimesLabel,
  errorCard,
  loadingCard,
} from './commuteAutocomplete.styles';
import { scrollFadeWrapper } from '../NeighborhoodSpoke/neighborhoodSpoke.styles';

type CommuteTimeCardsProps = {
  destination: CommuteDestination;
  commuteTimes: Array<CommuteTime>;
  isLoading?: boolean;
  error?: string;
  onEdit?: () => void;
  selectedMode?: TravelMode;
  onModeSelect?: (mode: TravelMode) => void;
  'data-testid'?: string;
};

/**
 * CommuteTimeCards - Display commute times for different travel modes
 *
 * Shows cards with travel times for car, walking, transit, and bicycle.
 * Replaces the autocomplete input after destination is selected.
 */
export const CommuteTimeCards: React.FC<CommuteTimeCardsProps> = ({
  destination,
  commuteTimes,
  isLoading = false,
  error,
  onEdit,
  selectedMode = 'driving',
  onModeSelect,
  'data-testid': testId,
}) => {
  // Travel mode display configuration
  const travelModeConfig: Record<TravelMode, TravelModeConfig> = {
    driving: {
      IconComponent: DrivingIcon,
      iconColor: '#007a70', // hpxTeal500
      label: 'by car',
      fallbackTime: '-- min',
    },
    walking: {
      IconComponent: WalkingIcon,
      iconColor: '#007a70', // hpxTeal500
      label: 'on foot',
      fallbackTime: '-- min',
    },
    transit: {
      IconComponent: TransitIcon,
      iconColor: '#007a70', // hpxTeal500
      label: 'by transit',
      fallbackTime: '-- min',
    },
    bicycling: {
      IconComponent: BicyclingIcon,
      iconColor: '#007a70', // hpxTeal500
      label: 'by bike',
      fallbackTime: '-- min',
    },
  };

  // Create lookup for commute times by mode
  const timesByMode = commuteTimes.reduce(
    (acc, time) => {
      acc[time.mode] = time;
      return acc;
    },
    {} as Record<string, CommuteTime>,
  );

  // Handle header click to allow editing
  const handleHeaderClick = () => {
    if (onEdit && !isLoading) {
      onEdit();
    }
  };

  // Handle card click to switch transportation mode
  const handleCardClick = (mode: TravelMode, hasData: boolean) => {
    if (onModeSelect && hasData && !isLoading) {
      onModeSelect(mode);
    }
  };

  if (error) {
    return (
      <div className={commuteResultsContainer} data-testid={testId}>
        <div className={commuteResultsHeader}>
          <div onClick={handleHeaderClick} style={{ cursor: onEdit ? 'pointer' : 'default' }}>
            Commute to {destination.name}
            {onEdit && <span style={{ fontSize: '14px', color: '#888' }}> (tap to edit)</span>}
          </div>
          {onEdit && (
            <button
              className={clearCommuteButton}
              onClick={onEdit}
              aria-label="Clear commute destination"
              data-testid="clear-commute-button"
            >
              Clear
            </button>
          )}
        </div>
        <div className={errorCard}>
          <div>Unable to calculate commute times</div>
          <div style={{ fontSize: '14px', color: '#888' }}>{error}</div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={commuteResultsContainer} data-testid={testId}>
        <div className={commuteResultsHeader} role="status" aria-live="polite" aria-atomic="true">
          Calculating commute to {destination.name}...
        </div>
        <div className={commuteTimesLabel}>Commute times</div>
        <div className={scrollFadeWrapper}>
          <div className={commuteResultsGridWithMargin}>
            {Object.entries(travelModeConfig).map(([mode, config]) => (
              <div key={mode} className={loadingCard}>
                <div className={commuteCardIcon}>
                  <config.IconComponent size="24" color={config.iconColor} />
                </div>
                <div className={commuteCardContent}>
                  <div className={commuteCardTime}>...</div>
                  <div className={commuteCardMode}>{config.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={commuteResultsContainer} data-testid={testId}>
      <div className={commuteResultsHeader}>
        <div onClick={handleHeaderClick} style={{ cursor: onEdit ? 'pointer' : 'default' }}>
          Commute to {destination.name}
          {onEdit && <span style={{ fontSize: '14px', color: '#888' }}> (tap to edit)</span>}
        </div>
        {onEdit && (
          <button
            className={clearCommuteButton}
            onClick={onEdit}
            aria-label="Clear commute destination"
            data-testid="clear-commute-button"
          >
            Clear
          </button>
        )}
      </div>

      <div className={commuteTimesLabel}>Commute times</div>
      <div className={scrollFadeWrapper}>
        <div className={commuteResultsGridWithMargin}>
          {Object.entries(travelModeConfig).map(([mode, config]) => {
            const commuteTime = timesByMode[mode];
            const hasData = commuteTime && commuteTime.duration > 0;
            const isActive = selectedMode === mode;
            const isClickable = hasData && onModeSelect && !isLoading;

            return (
              <div
                key={mode}
                className={cx(commuteCard, isActive && hasData && commuteCardActive)}
                data-testid={`commute-card-${mode}`}
                onClick={() => handleCardClick(mode as TravelMode, hasData)}
                style={{
                  opacity: hasData ? 1 : 0.5,
                  cursor: isClickable ? 'pointer' : 'default',
                }}
              >
                <div className={commuteCardIcon}>
                  <config.IconComponent size="24" color={config.iconColor} />
                </div>
                <div className={commuteCardContent}>
                  <div className={commuteCardTime}>
                    {hasData ? formatDuration(commuteTime.duration) : config.fallbackTime}
                  </div>
                  <div className={commuteCardMode}>{config.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
